import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { ReactNode, useState } from 'react';

// Mirrors the prototype's `.sec-head[data-fold]`: clickable header with a
// rotating chevron + title.

interface CollapsibleSectionProps {
  children: ReactNode;
  defaultExpanded?: boolean;
  // Either static nodes, or a render fn given `expand` so a header action (e.g.
  // Edit) can open the section as it fires — clicking Edit on a collapsed
  // section both expands it and enters edit mode.
  headerActions?: ReactNode | ((api: { expand: () => void }) => ReactNode);
  icon?: ReactNode;
  title: string;
}

export default function CollapsibleSection({
  children,
  defaultExpanded = true,
  headerActions,
  icon,
  title,
}: CollapsibleSectionProps) {
  const theme = useTheme();
  const c = theme.brandV2.colors;
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Box sx={{ borderTop: `1px solid ${c.treadGray7}` }}>
      {/* Clickable header bar — whole bar toggles; action clicks are isolated. */}
      <Box
        onClick={() => setExpanded((state) => !state)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          minHeight: 48,
          px: 1.5,
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'background-color 120ms ease',
          '&:hover': { bgcolor: c.treadGray8 },
          '&:hover .section-title': { color: c.treadBlue },
          '&:hover .section-chevron': { color: c.treadBlue },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {expanded ? (
            <KeyboardArrowDown
              className="section-chevron"
              sx={{ fontSize: 20, color: c.treadGray2, flexShrink: 0 }}
            />
          ) : (
            <KeyboardArrowRight
              className="section-chevron"
              sx={{ fontSize: 20, color: c.treadGray2, flexShrink: 0 }}
            />
          )}
          {icon && <Box sx={{ display: 'inline-flex', color: c.treadGray3 }}>{icon}</Box>}
          <Typography
            className="section-title"
            sx={{
              fontSize: '18px',
              fontWeight: 700,
              color: c.treadBlack,
              transition: 'color 120ms ease',
            }}
          >
            {title}
          </Typography>
        </Box>

        {headerActions && (
          <Box
            onClick={(event) => event.stopPropagation()}
            sx={{ ml: 'auto', display: 'inline-flex', gap: 1, cursor: 'default' }}
          >
            {typeof headerActions === 'function'
              ? headerActions({ expand: () => setExpanded(true) })
              : headerActions}
          </Box>
        )}
      </Box>

      <Collapse in={expanded} unmountOnExit>
        {/* 12px inset all round — matches the header's px so the body's left
            edge lines up with the chevron — with extra bottom padding setting
            the body off from the next section's divider. */}
        <Box sx={{ p: 1.5, pb: 3 }}>{children}</Box>
      </Collapse>
    </Box>
  );
}
